import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import {
  fetchBoxLabelGeneratePreview,
  type BoxLabelGeneratePreviewResponse,
} from "@/pages/manufacturing/boxLabelGenerateApi";
import {
  boxLabelLineScopeKey,
  boxLabelPreviewRequestKey,
  resolveBoxLabelPreviewDeviceSerial,
} from "@/pages/manufacturing/boxLabelGeneratePreviewState";

const PREVIEW_DEBOUNCE_MS = 50;

export function useBoxLabelGeneratePreviewLoader(input: {
  batchId: number | null;
  batchItemId: number | null;
  lineReadyToPrint: boolean;
  lineSerialsFingerprint: string;
  lineSerials: readonly { device_label_serial: string }[];
  printer: string;
  userSelectedSerial: string | null;
}): {
  effectiveSerial: string;
  preview: BoxLabelGeneratePreviewResponse | null;
  previewError: Error | null;
  isPreviewLoading: boolean;
} {
  const [preview, setPreview] = useState<BoxLabelGeneratePreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<Error | null>(null);
  const lineScopeKey = boxLabelLineScopeKey(input.batchId, input.batchItemId);
  const lineScopeKeyRef = useRef<string | null>(null);
  const scheduledPreviewKeyRef = useRef<string | null>(null);
  const activePreviewKeyRef = useRef<string | null>(null);

  const effectiveSerial = resolveBoxLabelPreviewDeviceSerial(
    input.userSelectedSerial ?? "",
    input.lineSerials,
  );

  const previewRequestKey =
    input.lineReadyToPrint && input.lineSerialsFingerprint !== ""
      ? boxLabelPreviewRequestKey({
          batchId: input.batchId,
          batchItemId: input.batchItemId,
          deviceLabelSerial: effectiveSerial,
          printer: input.printer,
        })
      : null;

  const previewMutation = useMutation({
    mutationFn: (request: {
      batchId: number;
      batchItemId: number;
      deviceLabelSerial: string;
      printer: string;
      requestKey: string;
    }) =>
      fetchBoxLabelGeneratePreview({
        batchId: request.batchId,
        batchItemId: request.batchItemId,
        deviceLabelSerial: request.deviceLabelSerial,
        printer: request.printer,
      }),
    onSuccess: (data, variables) => {
      if (activePreviewKeyRef.current !== variables.requestKey) {
        return;
      }
      setPreview(data);
      setPreviewError(null);
    },
    onError: (error, variables) => {
      if (activePreviewKeyRef.current !== variables.requestKey) {
        return;
      }
      setPreview(null);
      setPreviewError(error instanceof Error ? error : new Error("Preview failed."));
    },
  });

  useEffect(() => {
    if (lineScopeKey === lineScopeKeyRef.current) {
      return;
    }
    lineScopeKeyRef.current = lineScopeKey;
    scheduledPreviewKeyRef.current = null;
    activePreviewKeyRef.current = null;
    setPreview(null);
    setPreviewError(null);
  }, [lineScopeKey]);

  useEffect(() => {
    if (
      previewRequestKey == null ||
      input.batchId == null ||
      input.batchItemId == null ||
      effectiveSerial === ""
    ) {
      return;
    }

    if (scheduledPreviewKeyRef.current === previewRequestKey) {
      return;
    }

    const timer = window.setTimeout(() => {
      scheduledPreviewKeyRef.current = previewRequestKey;
      activePreviewKeyRef.current = previewRequestKey;
      previewMutation.mutate({
        batchId: input.batchId!,
        batchItemId: input.batchItemId!,
        deviceLabelSerial: effectiveSerial,
        printer: input.printer,
        requestKey: previewRequestKey,
      });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    effectiveSerial,
    input.batchId,
    input.batchItemId,
    input.printer,
    previewRequestKey,
  ]);

  return {
    effectiveSerial,
    preview,
    previewError,
    isPreviewLoading: previewMutation.isPending,
  };
}
